import type { RouteLocation } from "@openclaw/uirouter";
import { definePage } from "@openclaw/uirouter";
import type { AgentsListResult } from "../../api/types.ts";
import { routePageSpec } from "../../app-route-paths.ts";
import type { ApplicationContext, ApplicationGatewaySnapshot } from "../../app/context.ts";
import { selectableAgentsList } from "../../lib/agents/display.ts";
import { resolveAgentsRouteLocation, type AgentsRouteLocation } from "./route-location.ts";

export type AgentsRouteData = AgentsRouteLocation & {
  // Client identity alone cannot distinguish provider replacement or reconnect epochs.
  gateway: ApplicationContext["gateway"];
  gatewaySnapshot: ApplicationGatewaySnapshot;
  agentsList: AgentsListResult | null;
  error: string | null;
};

async function loadAgentsRouteData(
  context: ApplicationContext,
  location: RouteLocation,
): Promise<AgentsRouteData> {
  const route = resolveAgentsRouteLocation(location, context.basePath);
  const gateway = context.gateway;
  const gatewaySnapshot = gateway.snapshot;
  const rawAgentsList = context.agents.state.agentsList ?? (await context.agents.ensureList());
  const agentsList = rawAgentsList ? selectableAgentsList(rawAgentsList) : null;
  return {
    ...route,
    gateway,
    gatewaySnapshot,
    agentsList,
    error: context.agents.state.agentsError,
  };
}

export const page = definePage({
  ...routePageSpec("agents"),
  loaderDeps: (context: ApplicationContext, location: RouteLocation) => {
    const route = resolveAgentsRouteLocation(location, context.basePath).location;
    return `${route.pathname}\u0000${route.search}\u0000${route.hash}`;
  },
  // Cached selections must settle without a module-loading delay that retains stale controls.
  loader: (context: ApplicationContext, { location }) => loadAgentsRouteData(context, location),
  component: () => import("./agents-page.ts"),
});
